import {
  DataProvider,
  GetListResult,
  GetManyParams,
  GetManyResult,
  GetOneParams,
  GetOneResult,
  UpdateParams,
  UpdateResult,
  CreateParams,
  CreateResult,
  Identifier,
  DeleteParams,
  DeleteResult,
} from 'react-admin'
import { TicketAPIGetListParams } from '../../common/ticket-api-get-list.params'
import {
  filterParamsComposer,
  queryParamsComposer,
} from '../../common/get-by-conditions.utils'
import { mapSortEngineMediaFileParam } from './engine-media-file-sort.mapper'
import {
  get,
  getWithPagination,
  patch,
  post,
  put,
  remove,
} from '../../common/fetch.utils'
import filterMapper from './engine-media-file-filter.mapper'
import { EngineMediaFileDto } from '../../dto/engine-media/engine-media-file.dto'
import { ENGINE_MEDIA_FILES_URL } from '../../api-urls'
import { MediaContentTypes } from '../../enum/MediaContentTypes'
import { CacheableDataProviderExtensionResult } from '../../common/data-provider'

const provider = {
  getList: async (
    resource: string,
    { filter, sort, pagination }: TicketAPIGetListParams,
  ): Promise<GetListResult<EngineMediaFileDto>> => {
    const filterParams = `o=>${filterParamsComposer('o', filter, filterMapper)}`
    const pathParams = queryParamsComposer(
      sort,
      pagination,
      mapSortEngineMediaFileParam,
    )
    const path = `/${filterParams}${(pathParams && `?${pathParams}`) || ''}`

    const {
      data,
      range: { total },
    } = await getWithPagination<EngineMediaFileDto[]>(
      `${ENGINE_MEDIA_FILES_URL}/GetByConditions`,
      path,
    )

    return Promise.resolve({
      data,
      total,
    })
  },
  getOne: async (
    resource: string,
    { id }: GetOneParams,
  ): Promise<GetOneResult<EngineMediaFileDto>> => {
    const data = await get<EngineMediaFileDto>(ENGINE_MEDIA_FILES_URL, `/${id}`)
    return Promise.resolve({
      data,
    })
  },
  getMany: async (
    resource: string,
    { ids }: GetManyParams,
  ): Promise<GetManyResult<EngineMediaFileDto>> => {
    const { data } = await getWithPagination<EngineMediaFileDto[]>(
      `${ENGINE_MEDIA_FILES_URL}/GetByConditions/`,
      `e=>new int[] {${ids.toString()}}.Contains(e.Id)`,
    )
    return Promise.resolve({
      data,
    })
  },
  create: async (
    resource: string,
    { data }: CreateParams<CreateEngineMediaFileRequest>,
  ): Promise<CreateResult<EngineMediaFileDto>> => {
    const file = await post<
      CreateEngineMediaFileRequest[],
      EngineMediaFileDto[]
    >(ENGINE_MEDIA_FILES_URL, [data])
    return {
      data: file[0],
    }
  },
  update: async (
    resource: string,
    { id, data, previousData }: UpdateParams<EngineMediaFileDto>,
  ): Promise<UpdateResult> => {
    const update = await patch<
      { id: Identifier; [n: string]: any },
      EngineMediaFileDto
    >(ENGINE_MEDIA_FILES_URL, {
      id,
      description:
        data.description !== previousData.description
          ? data.description
          : undefined,
      engineMediaActionId:
        data.engineMediaActionId !== previousData.engineMediaActionId
          ? data.engineMediaActionId
          : undefined,
      mediaContentType:
        data.mediaContentType !== previousData.mediaContentType
          ? data.mediaContentType
          : undefined,
      filePathId:
        data.filePathId !== previousData.filePathId
          ? data.filePathId
          : undefined,
    })
    return Promise.resolve({ data: update })
  },
  delete: async (
    resource: string,
    { id }: DeleteParams,
  ): Promise<DeleteResult> => {
    await remove<undefined>(ENGINE_MEDIA_FILES_URL, `/Delete/${id}`)
    return Promise.resolve({ data: { id } })
  },
  attachPacks: async (
    resource: string,
    params: FilesPacksDto,
  ): Promise<CacheableDataProviderExtensionResult<FilesPacksDto>> => {
    const filePacks = await put<FilesPacksDto, FilesPacksDto>(
      `${ENGINE_MEDIA_FILES_URL}/AttachPacks`,
      params,
    )
    return {
      data: filePacks,
    }
  },
  detachPacks: async (
    resource: string,
    params: FilesPacksDto,
  ): Promise<CacheableDataProviderExtensionResult<FilesPacksDto>> => {
    const filePacks = await put<FilesPacksDto, FilesPacksDto>(
      `${ENGINE_MEDIA_FILES_URL}/DetachPacks`,
      params,
    )
    return {
      data: filePacks,
    }
  },
} as PacksDataProvider

interface PacksDataProvider extends DataProvider {
  attachPacks: (
    resource: string,
    params: FilesPacksDto,
  ) => Promise<CacheableDataProviderExtensionResult<FilesPacksDto>>

  detachPacks: (
    resource: string,
    params: FilesPacksDto,
  ) => Promise<CacheableDataProviderExtensionResult<FilesPacksDto>>
}

interface FilesPacksDto {
  readonly engineMediaPackId: number
  readonly engineMediaFilesIds: number[]
}

export interface CreateEngineMediaFileRequest {
  readonly description: string
  readonly engineMediaActionId: number
  readonly mediaContentType: MediaContentTypes
  readonly filePathId: number
}

export default provider
